import { useState, useEffect } from 'react';

import Flag from 'react-flags';
import i18next from 'i18next';
import toast from 'react-hot-toast';
import { Helmet } from 'react-helmet';
import { useFormik } from 'formik';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';

import Yup from '../../utils/yupExtensions';
import Button from '../../components/UI/Buttons/Button/Button';
import styles from './Job.module.css';
import SideBar from '../../components/UI/Common/Job/SideBar/SideBar';
import BasicPopup from '../../components/UI/Popups/BasicPopup/BasicPopup';
import CVPairList from '../../components/UI/Common/CVPair/CVPairListList/CVPairList';
import BlockLoader from '../../components/UI/Loaders/BlockLoader';
import UploadResume from '../../components/UI/Common/Job/UploadResume/UploadResume';
import CreateJobAlert from '../../components/UI/Common/Job/CreateJobAlert/CreateJobAlert';
import FormRichTextField from '../../components/UI/FormUI/FormRichTextField/FormRichTextField';
import VerificationBanner from '../../components/UI/Common/Jobs/VerificationBanner/VerificationBanner';
import JobDetailSection from '../../components/UI/Common/Job/JobsDetail/JobsDetail';
import { useGetSkillsHook } from '../../utils/utilityHooks';
import { getTimeAgo, getCurrencySymbol, } from '../../utils/formatHelpers';
import { updateFavourites, updateApplications } from '../../app/features/userSlice';
import {
    getJob,
    sendJobProposal,
    addJobToFavourites,
    removeJobFromFavourites,
    updateJobField,
    incrementJobViews
} from '../../services/jobService';
import BubbleButton from '../../components/UI/Buttons/BubbleButton/BubbleButton';
import { createChatroom } from '../../services/chatService';

function Job() {
    const { slug } = useParams();
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    let options = useGetSkillsHook()

    const user = useSelector(state => state.user.user);
    const userToken = useSelector(state => state.user.token);
    const [job, setJob] = useState(null);
    const [isApplyPopupOpen, setIsApplyPopupOpen] = useState(false);
    const [isSelectCVPopupOpen, setIsSelectCVPopupOpen] = useState(false);
    const [formLoading, setFormLoading] = useState(false);
    const skills = useSelector((state) => state.user.skills);
    const currentLanguage = i18next.language;
    const [selectedResume, setSelectedResume] = useState("");
    const [agreeTermsAndCondition, setAgreeTermsAndCondition] = useState(false);
    const [isSuccessPopupOpen, setIsSuccessPopupOpen] = useState(false);
    const [statusMessage, setStatusMessage] = useState("");
    const [isStatusMessagePopupOpen, setIsStatusMessagePopupOpen] = useState(false);

    const generateCVPairs = (user) => {
        const cvPairs = [];
        let primarySet = false; // Track if isPrimary has been set to true for any CV pair
        if (user.verification_status == 'approved') {
            for (let i = 0; i < 5; i++) {
                const cvFileKey = `cv_file_${i}`;
                const cvRefLetterKey = `cv_ref_letter_${i}`;
                if (user[cvFileKey] && user[cvRefLetterKey]) {
                    const CVPairId = Date.now() + i
                    const isPrimary = !primarySet && user[cvFileKey] === user['primary_cv_file'];
                    const extractExtension = (filePath) => {
                        const fileNameMatch = filePath.match(/(?:[^/\\]+\/)*([^/\\]+\.\w+)$/);
                        if (fileNameMatch) {
                            const fileName = fileNameMatch[1];
                            const ext = fileName.split('.').pop();
                            return ext; // Returns the extension, e.g., "pdf"
                        }
                        return ''; // If no match found, return an empty string
                    };
                    const extractName = (filePath) => {
                        const extension = extractExtension(filePath);
                        const nameMatch = filePath.match(/name:([^-.]+)/);
                        return nameMatch ? nameMatch[1] + "." + extension : '';
                    };
                    setSelectedResume(CVPairId)
                    cvPairs.push({
                        id: CVPairId,
                        isPrimary: isPrimary,
                        cv_file: {
                            name: extractName(user[cvFileKey]),
                            path: user[cvFileKey]
                        },
                        cv_ref_letter: {
                            name: extractName(user[cvRefLetterKey]),
                            path: user[cvRefLetterKey],
                        }
                    });

                    if (isPrimary) {
                        primarySet = true;
                    }
                }

                // If no match was found, set the first entry's isPrimary to true
                if (!primarySet && cvPairs.length > 0) {
                    cvPairs[0].isPrimary = true;
                }
            }
        } else {
            cvPairs.push({
                id: Date.now(),
                isPrimary: true,
                cv_file: {
                    name: user.cv_file,
                    path: user.cv_file
                },
                cv_ref_letter: {
                    name: user.cv_ref_letter,
                    path: user.cv_ref_letter
                },
                video_cv: {
                    name: user.video_cv,
                    path: user.video_cv
                }
            })
        }
        return cvPairs;
    };
    const formik = useFormik({
        initialValues: {
            proposal_text: '',
            cv_Pair: '', // Use the generated CV pairs
            // cv_Pair: generateCVPairs(user), // Use the generated CV pairs

        },
        validationSchema: Yup.object({
            proposal_text: Yup.string().min(100, t('job.must_be_at_least_100_characters')).required(t('general.UI.required'))
        }),
        onSubmit: async (values) => {
            if (formLoading) {
                return;
            }

            setFormLoading(true);
            const proposalData = {
                job_id: job.id,
                token: userToken,
                status: "New",
                proposal_text: values.proposal_text
            }
            const selectedResumrePair = values.cv_Pair.filter((e) => e.isPrimary === true)
            if (selectedResumrePair?.length > 0) {
                if (user?.verification_status == 'approved') {
                    proposalData.proposal_cv = selectedResumrePair[0]?.cv_file?.path
                    proposalData.proposal_cover_letter = selectedResumrePair[0]?.cv_ref_letter?.path
                }
            }
            if (user?.verification_status !== 'approved') {
                proposalData.proposal_cv = user.cv_file
                proposalData.proposal_cover_letter = user.cv_ref_letter
            }
            toast.promise(sendJobProposal(proposalData), {
                loading: t('general.UI.please_wait'),
                success: <b>{t('general.UI.success')}</b>,
                error: (err) => {
                    return <b>{err.response.data.error}</b>;
                },
            })
                .then((response) => {
                    dispatch(updateApplications([...user.applications, parseInt(job.id, 10)]));
                    setFormLoading(false);
                    setIsApplyPopupOpen(false);
                })
                .catch((error) => {

                    if (error?.response?.data?.field) {
                        formik.setErrors({
                            [error.response.data.field]: error.response.data.error
                        });
                    }

                    setFormLoading(false);

                });

        },
    });

    useEffect(() => {

        getJob(slug)
            .then((response) => {
                response.skills = response.skills.map(skill => {
                    let skillData = skills?.filter((e) => e.value == skill.skill_code)[0]
                    if (skills.length > 0 && skillData) {
                        return {
                            id: skill.skill_code + '-' + skill.job_id,
                            job_id: skill.job_id,
                            code: currentLanguage === "en" ? skillData.labelen : skillData.labelar
                        }
                    } else {
                        return {
                            id: skill.skill_code + '-' + skill.job_id,
                            job_id: skill.job_id,
                            code: skill.skill_code
                        }
                    }
                });
                setJob(response);
            })
            .catch((error) => {
                console.log(error);
            });

    }, [slug, options]);

    const handleBackClick = () => {
        navigate(-1);
    };

    const handleApplyClick = (e) => {
        e.preventDefault();

        if (!user) {
            navigate('/login');
            return;
        }

        // Check job status before allowing application
        if (job.status !== 'opened' && job.status !== 'published' && job.status !== 'active') {
            let message = "";
            switch (job.status) {
                case 'paused':
                    message = t('job_status_messages.paused');
                    break;
                case 'closed':
                    message = t('job_status_messages.closed');
                    break;
                case 'expired':
                    message = t('job_status_messages.expired');
                    break;
                default:
                    message = t('job_status_messages.default');
            }
            setStatusMessage(message);
            setIsStatusMessagePopupOpen(true);
            return;
        }

        if (user?.verification_status == 'approved') {
            setIsSelectCVPopupOpen(true);
        } else {
            setIsApplyPopupOpen(true);
        }
    };

    const handleAddToFavourites = () => {
        addJobToFavourites({
            job_id: job.id,
            token: userToken
        })
            .then((response) => {
                dispatch(updateFavourites([...user.favourites, parseInt(job.id, 10)]));
                toast.success(t('job.job_added_to_favorites'));
            })
            .catch((error) => {

                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }

            });
    };

    const handleRemoveFromFavourites = () => {
        removeJobFromFavourites({
            job_id: job.id,
            token: userToken
        })
            .then((response) => {
                dispatch(updateFavourites(user.favourites.filter(item => item !== parseInt(job.id, 10))));
                toast.success(t('job.job_removed_from_favorites'));
            })
            .catch((error) => {

                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }

            });
    };

    const handlePauseClick = () => {
        updateJobField({
            job_id: job.id,
            token: userToken,
            field: 'status',
            value: (job.status === 'paused' || job.status === 'closed') ? 'active' : 'paused'
        })
            .then((response) => {
                setJob((prevState) => ({
                    ...prevState,
                    status: (prevState.status === 'paused' || prevState.status === 'closed') ? 'active' : 'paused'
                }));
            })
            .catch((error) => {

                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }

            });
    };

    const handleCloseClick = () => {
        updateJobField({
            job_id: job.id,
            token: userToken,
            field: 'status',
            value: 'closed'
        })
            .then((response) => {
                setJob((prevState) => ({
                    ...prevState,
                    status: 'closed'
                }));
            })
            .catch((error) => {

                if (error?.response?.data?.error) {
                    toast.error(error.response.data.error);
                }

            });
    };

    const infoTableData = [];
    let date = '';

    if (job) {

        if (job?.createdAt) {
            const date_info = getTimeAgo(new Date(job.createdAt));
            date = t('general.UI.posted') + ' ';

            if (date_info.unit_plural === 'second' || date_info.unit_plural === 'seconds') {
                date += t('general.UI.just_now');
            }
            else {
                date += date_info.count + ' ' + t('general.UI.' + date_info.unit_plural) + ' ' + t('general.UI.ago');
            }

        }

        if (job?.country) {
            infoTableData.push({
                key: 'country',
                label: t('job.country'),
                value: (
                    <>
                        <Flag
                            name={job.country}
                            format="svg"
                            shiny={false}
                            basePath="/vendor/flags"
                        /> {t('general.country.' + job.country)}
                    </>
                )
            });
        }

        if (job?.city) {

            let city = job.city;

            if (t('general.city.' + city) !== 'general.city.' + city) {
                city = t('general.city.' + city);
            }

            infoTableData.push({
                key: 'city',
                label: t('job.city_town_village'),
                value: city
            });

        }

        if (job?.job_type) {
            infoTableData.push({
                key: 'job_type',
                label: t('job.job_type'),
                value: t('general.UI.' + job.job_type)
            });
        }

        let salary = '';
        let salary_period = t('general.UI.month');
        if (job?.currency && job?.currency !== 'undefined') {
            const currency = getCurrencySymbol(job.currency);
            if (job.salary_low !== job.salary_high) {
                salary = currency + job.salary_low + ' - ' + currency + job.salary_high;
            }
            else {
                salary = currency + job.salary_low;
            }
        } else {
            const currency = getCurrencySymbol('usd');
            salary = currency + job.salary_from + ' - ' + currency + job.salary_to;
        }

        if (job?.salaryrate) salary_period = t('CompensationAndbenefits.' + job?.salaryrate[0].toUpperCase() + job?.salaryrate.slice(1, job?.salaryrate.length));
        else if (job?.payment_type === 'hourly') salary_period = t('general.UI.hour');

        if (salary !== '') {
            infoTableData.push({
                key: 'salary',
                label: t('job.salary'),
                value: (
                    <>
                        {salary} /<span style={{ opacity: 0.5, marginLeft: 5, marginRight: 5 }}>{salary_period}</span>
                    </>
                )
            });
        }

        let working_hours = '';

        if (job.hours_low !== job.hours_high) {
            working_hours = job.hours_low + ' - ' + job.hours_high;
        }
        else {
            working_hours = job.hours_low;
        }

        if (working_hours !== '') {
            infoTableData.push({
                key: 'working_hours',
                label: t('job.working_hours_per_week'),
                value: working_hours
            });
        }

    }
    const [cvPair, setCVPairs] = useState("");
    useEffect(() => {



        if (user) {
            setCVPairs(generateCVPairs(user))
        }

    }, [user])
    const [hasIncremented, setHasIncremented] = useState(false);

    useEffect(() => {

        const viewIncrement = async () => {
            if (!hasIncremented && job?.id) {
                await incrementJobViews(job.id);
                setHasIncremented(true); // Ensure it runs only once
            }
        };

        viewIncrement();
    }, [job?.id]); // Run only when job.id changes
    const handlePopupClose = (popupName) => {
        setIsSelectCVPopupOpen(false);
        setIsApplyPopupOpen(false);
        if (popupName === 'SuccessPopup') {
            setIsSuccessPopupOpen(false);
        }
    }
    const handleSendMessage = async () => {
        if (job.employer_id && user.id) {
            await createChatroom(job.employer_id, user.id);
            navigate('/chat', {
                state: {
                    employerId: job.employer_id,
                    jobSeekerId: user.id,
                }
            })
        }
    }
    const [hasSkillMatch, setHasSkillMatch] = useState(false);
    useEffect(() => {
        const jobSkills = job?.skills?.map(skill => skills.find(s => s.labelen === skill.code))
        const userSkills = user?.skills?.map(skill => skills.find(s => s.value === Number(skill.code)))
        if (jobSkills?.length > 0 && userSkills?.length > 0) {
            jobSkills?.forEach(jobSkill => {
                userSkills?.forEach(userSkill => {
                    if (jobSkill?.value === userSkill?.value) {
                        setHasSkillMatch(true)
                    }
                })
            })
        }
    }, [job?.skills, user?.skills])
     return (
        <>
            {job ? (
                <>
                    <Helmet>
                        <title>FindCV - {job.title}</title>
                        <meta name="description" content={job.description} />
                    </Helmet>

                    {user?.account_type !== 'employer' && user?.verification_status !== 'approved' &&
                        <VerificationBanner checkMark={true} />
                    }
                    <div className={styles.jobDetailWrapper}>
                        <div className={styles.contentContainer}>
                            {job && <JobDetailSection
                                    hasSkillMatch={hasSkillMatch}
                                    agreeTermsAndCondition={agreeTermsAndCondition}
                                cvPair={cvPair}
                                selectedResume={selectedResume}
                                job={job} />}
                            <CreateJobAlert />
                            {user?.applications?.includes(parseInt(job.id, 10)) ? <></> :
                                user?.account_type == 'jobseeker' &&
                                job && <UploadResume
                                    agreeTermsAndCondition={agreeTermsAndCondition}
                                    setAgreeTermsAndCondition={setAgreeTermsAndCondition}
                                    cvPair={cvPair}
                                    hasSkillMatch={hasSkillMatch}
                                    setCVPair={setCVPairs}
                                    selectedResume={selectedResume}
                                    isSuccessPopupOpen={isSuccessPopupOpen}
                                    setIsSuccessPopupOpen={setIsSuccessPopupOpen}
                                    setSelectedResume={setSelectedResume}
                                    job={job} />
                            }

                        </div>
                        {job && <SideBar job={job} />}
                    </div>

                    <BasicPopup isOpen={isSelectCVPopupOpen} closePopup={handlePopupClose}>

                        {formik.values?.cv_Pair?.length > 0 &&
                            <>
                                <div className={styles.scrollContainer} >
                                    <CVPairList
                                        isDark
                                        formik={formik}
                                        disableRemove
                                        cv_Pair={formik.values?.cv_Pair}
                                    />
                                </div>
                                <div style={{ textAlign: 'center' }}>
                                    <Button onClick={() => {
                                        setIsSelectCVPopupOpen(false);
                                        setIsApplyPopupOpen(true);
                                    }}>
                                        {formLoading ? t('general.UI.loading') : t('general.UI.submit')}
                                    </Button>
                                </div>
                            </>
                        }
                    </BasicPopup>
                    <BasicPopup isOpen={isApplyPopupOpen} closePopup={handlePopupClose}>
                        <form onSubmit={formik.handleSubmit}>
                            <div>
                                <FormRichTextField
                                    name="proposal_text"
                                    label={t('job.proposal_message')}
                                    hasBorder
                                    onFormikChange={formik.handleChange}
                                    value={formik.values.proposal_text}
                                    error={formik.touched.proposal_text && formik.errors.proposal_text}
                                />
                            </div>
                            <div style={{ textAlign: 'center' }}>
                                <Button type="submit">
                                    {formLoading ? t('general.UI.loading') : t('general.UI.submit')}
                                </Button>
                            </div>
                        </form>
                    </BasicPopup>
                    <BasicPopup
                        isOpen={isSuccessPopupOpen}
                        closePopup={() => handlePopupClose('SuccessPopup')}
                    >
                        <div className={styles.successPopup}>
                            <h3>{user?.verification_status === 'approved' ?
                                `${t('UploadResume.thanks_for_apply')} ${job.title}. ${t('UploadResume.application_forwarded')}. ${t('UploadResume.send_message_description')}` :
                                `${t('UploadResume.thanks_for_apply')} ${job.title}. ${t('UploadResume.application_forwarded')}. ${t('UploadResume.wait_for_response')}`
                            }</h3>
                            <div className={styles.popupButtons}>
                                <BubbleButton
                                    onClick={() => handlePopupClose('SuccessPopup')}
                                    dark
                                    headerDarkButton
                                    type="default"
                                    small
                                >
                                    {t('general.UI.okay')}
                                </BubbleButton>

                                {user?.verification_status === 'approved' &&
                                    <BubbleButton
                                        onClick={handleSendMessage}
                                        dark
                                        headerDarkButton
                                        type="default"
                                        small
                                    >
                                        {t('EmployerViewProfile.message')}
                                    </BubbleButton>
                                }
                            </div>
                        </div>
                    </BasicPopup>
                    <BasicPopup
                        isOpen={isStatusMessagePopupOpen}
                        closePopup={() => setIsStatusMessagePopupOpen(false)}
                    >
                        <div className={styles.successPopup}>
                            <h3>{statusMessage}</h3>
                            <div className={styles.popupButtons}>
                                <BubbleButton
                                    onClick={() => setIsStatusMessagePopupOpen(false)}
                                    dark
                                    headerDarkButton
                                    type="default"
                                    small
                                >
                                    {t('general.UI.okay')}
                                </BubbleButton>
                            </div>
                        </div>
                    </BasicPopup>
                </>
            ) : (
                <>
                    <Helmet>
                        <title>FindCV - Job Posting</title>
                    </Helmet>
                    <div className={styles.wrapper}>
                        <div className={styles.contentContainer}>
                            <BlockLoader height={500} marginBottom={20} />
                        </div>
                        <div className={styles.sideBarBlockLoadContainer}>
                            <BlockLoader height={500} marginBottom={20} />
                        </div>
                    </div>
                    <div className={styles.wrapper}>
                        <div className={styles.contentContainer}>
                            <BlockLoader height={500} marginBottom={20} />
                        </div>
                        <div className={styles.sideBarBlockLoadContainer}>
                            <BlockLoader height={500} marginBottom={20} />
                        </div>
                    </div>
                </>
            )
            }
        </>
    );

}

export default Job;